<script setup lang="ts">
import { nextTick, ref, watch } from 'vue'
import type { CannedItem } from '../../api/types'
import { firstLine } from '../../lib/slash'
import { sendMediaOf } from '../../stores/library'
import Icon from '../ui/Icon.vue'
import MediaThumb from '../library/MediaThumb.vue'

/** The "/" answer list above the composer. The composer owns the keyboard (↑ ↓ Enter Tab Esc). */
const props = defineProps<{
  items: CannedItem[]
  active: number
  query: string
  loading: boolean
  canManage: boolean
}>()
const emit = defineEmits<{ pick: [item: CannedItem]; hover: [index: number] }>()

const list = ref<HTMLElement | null>(null)
watch(
  () => props.active,
  () =>
    void nextTick(() => {
      const el = list.value?.querySelector<HTMLElement>(`[data-i="${props.active}"]`)
      el?.scrollIntoView({ block: 'nearest' })
    }),
)
</script>

<template>
  <div class="sp" role="listbox" :aria-label="`คำตอบสำเร็จรูป ${query ? '/' + query : ''}`" @mousedown.prevent>
    <div v-if="loading" class="sp-bar" aria-hidden="true"></div>
    <div class="sp-head">
      <Icon name="zap" />
      <b>คำตอบสำเร็จรูป</b>
      <span v-if="query" class="sp-q">ค้นหา <span class="mono">/{{ query }}</span></span>
      <span class="grow"></span>
      <span class="sp-keys"><kbd>↑</kbd><kbd>↓</kbd> เลือก · <kbd>Enter</kbd> ใส่ · <kbd>Esc</kbd> ปิด</span>
    </div>
    <div v-if="items.length" ref="list" class="sp-list">
      <button
        v-for="(it, i) in items"
        :key="it.id"
        type="button"
        class="sp-row"
        :class="{ on: i === active }"
        role="option"
        :aria-selected="i === active"
        :data-i="i"
        @mouseenter="emit('hover', i)"
        @click="emit('pick', it)"
      >
        <span class="sp-sc mono">/{{ it.shortcut }}</span>
        <span class="sp-body">
          <span class="sp-t">{{ it.title }}</span>
          <span v-if="it.text" class="sp-x">{{ firstLine(it.text) }}</span>
        </span>
        <span v-if="sendMediaOf(it).length" class="sp-media" :aria-label="`มีรูปหรือวิดีโอ ${sendMediaOf(it).length} รายการ`">
          <MediaThumb v-for="m in sendMediaOf(it).slice(0, 3)" :key="m.id" :asset="m" :size="36" />
          <span v-if="sendMediaOf(it).length > 3" class="sp-more mono">+{{ sendMediaOf(it).length - 3 }}</span>
        </span>
      </button>
    </div>
    <div v-else-if="!loading" class="sp-empty">
      <span>ไม่พบคำตอบ<template v-if="query"> “/{{ query }}”</template></span>
      <RouterLink v-if="canManage" class="sp-link" :to="{ name: 'config', params: { section: 'canned' } }">เพิ่มคำตอบในตั้งค่า</RouterLink>
    </div>
  </div>
</template>

<style scoped>
.sp {
  position: absolute;
  left: 16px;
  right: 16px;
  bottom: calc(100% - 4px);
  z-index: 20;
  display: flex;
  flex-direction: column;
  max-height: min(360px, 55vh);
  background: #fff;
  border: 1px solid #cfe3d8;
  border-radius: 12px;
  box-shadow: 0 10px 28px rgba(23, 33, 28, 0.14);
  overflow: hidden;
}
.sp-bar {
  position: absolute;
  top: 0;
  left: 0;
  height: 2px;
  width: 40%;
  background: var(--ac);
  animation: sp-slide 1s ease-in-out infinite;
}
@keyframes sp-slide {
  from {
    transform: translateX(-100%);
  }
  to {
    transform: translateX(250%);
  }
}
.sp-head {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px 8px;
  border-bottom: 1px solid var(--line2);
  font-size: 13px;
  line-height: 20px;
}
.sp-head > :deep(.ic) {
  width: 15px;
  height: 15px;
  color: var(--ac);
}
.sp-q {
  color: var(--mut);
}
.sp-keys {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  color: var(--mut);
  font-size: 12px;
  line-height: 18px;
  white-space: nowrap;
}
kbd {
  font: 600 12px/18px var(--mono);
  padding: 0 5px;
  border-radius: 5px;
  background: #f4f6f5;
  border: 1px solid #d3ddd8;
  border-bottom-width: 2px;
  color: var(--ink);
}
.sp-list {
  overflow-y: auto;
  padding: 4px;
}
.sp-row {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  width: 100%;
  padding: 8px 10px;
  border-radius: 8px;
}
.sp-row.on {
  background: var(--acs);
}
.sp-sc {
  flex: none;
  min-width: 72px;
  padding: 1px 8px;
  border-radius: 6px;
  background: #eef2f0;
  color: var(--ac2);
  font-size: 12.5px;
  font-weight: 600;
  line-height: 20px;
  text-align: center;
}
.sp-row.on .sp-sc {
  background: #fff;
}
.sp-body {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 1px;
}
.sp-t {
  font-size: 13.5px;
  font-weight: 600;
  line-height: 21px;
  color: var(--ink);
}
.sp-x {
  font-size: 12.5px;
  line-height: 19px;
  color: var(--mut);
  overflow-wrap: anywhere;
}
.sp-media {
  flex: none;
  display: inline-flex;
  align-items: center;
  gap: 4px;
}
.sp-more {
  font-size: 12px;
  line-height: 18px;
  color: var(--mut);
}
.sp-empty {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px;
  color: var(--mut);
  font-size: 13px;
  line-height: 20px;
}
.sp-link {
  color: var(--ac2);
  font-weight: 600;
}
</style>
